import axios, {AxiosResponse} from "axios";
import {appEnv} from "../env";
import {
    GetHomePageDataResponse,
    IGetActiveActionsResponse,
    IGetAllConfigResponse,
    IGetLicenseCollectionsResponse,
    IGetUserInfoResponse,
    IGetUserPassesResponse,
    ILicensedNFT,
    ILoginResponse,
    IMintIPInfo,
    IMythServerResponse,
    INFTBrand,
    INFTBrandItem,
    IPagination, IPInfluence,
    IPRsp
} from "../bean/ResponseBean";
import {handleTokenInvalid} from "./TokenHandler";
import {MythServerResultCode} from "../constant/CommonConstant";
import {handleMythServerError} from "./ApiErrorHandler";
import {
    IGetAllIPsRequest,
    IGetDerivativeWorksForLicenseNFTRequest,
    ILicenseNFTsRequest, IPLInfo,
    IUserInfoRequest
} from "../bean/RequestBean";
import {getLocalStorageTokenKey} from "../stores/web3Store";

export interface INonceRequestParams {
    address: string,
    signature?: string,
}

const mythServerApis = {
    mintNew: {
        joinMintNew: "/mintNew/joinMintNew",
        getActiveActions: "/mintNew/getActiveActions",
        getPassStatus: "/mintNew/getPassStatus",
        getAllConfig: "/mintNew/getAllConfig",
    },
    login: {
        getNonce: "/login/getNonce",
        requestLogin: "/login/requestLogin"
    },
    ipMarket: {
        getLicenseCollections: "/ipMarket/getLicenseCollections",
        getHomePageData: "/ipMarket/getHomePageData",
        getAllIps: "/ipMarket/getAllIps",
        getLicensedNFT: "/ipMarket/getLicensedNFT",
        getMintIPInfo: "/ipMarket/getMintIPInfo",
        getIPsForLicensedNFT: "/ipMarket/getIPsForLicensedNFT",
        getIPInfluences: "/ipMarket/getIPInfluences"
    },
    ipLicense: {
        licenseNFTs: "/ipLicense/licenseNFTs",
        activeLicenseNFT: "/ipLicense/activeLicenseNFT",
        addIPL: "/ipLicense/addIPL",
        updateIPL: "/ipLicense/updateIPL",
        deleteIPL: "/ipLicense/deleteIPL",
        updateIPLStatus: "/ipLicense/updateIPLStatus",
        getMyIPLs: "/ipLicense/getMyIPLs",
        getLicensedNFTTokenIds:"/ipLicense/getLicensedNFTTokenIds",
        getIPLByIPId:"/ipLicense/getIPLByIPId"
    },
    user: {
        getUserInfo: "/user/getUserInfo",
        updateUserInfo: "/user/updateUserInfo",
        getMyActivatedIPs:"/user-center/my-activated-ips"
    },
    nftBrand: {
        getNFTBrands: "/nft-brand/nft-brands",
        getNFTBrand: "/nft-brand/nft-brands/"
    }
};

//to fix CROS for myth server
axios.defaults.withCredentials = true;

const getMythServerApi = (api: string) => {
    return appEnv.mythServer.apiPrefix + api;
};

function tokenHeaderConfig(account: string, contentType = ContentType.FORM) {
    const token = localStorage.getItem(getLocalStorageTokenKey(account));
    return {
        headers: {
            "token": token!,
            "content-type": contentType
        }
    }
}

export const ContentType = {
    JSON: "application/json;charset=UTF-8",
    FORM: "application/x-www-form-urlencoded;charset=utf-8"
};

/**
 * response拦截器，处理以下情况:
 * 1.处理业务错误
 * 2.token失效
 */
axios.interceptors.response.use(
    res => {
        if (res.data.resultCode && res.data.resultCode !== MythServerResultCode.SUCCESS) {
            handleMythServerError(res.data.resultMessage);
        }
        return res
    }, err => {
        //catch error
        if (err.response && err.response.status === 403
            && err.response.data && err.response.data.resultCode === 501) {
            handleTokenInvalid();
        }
        return err
    }
);

export default class MythServerApi {

    /*********************************************************** Login *********************************************************************/
    public async getNonceWithAddress(params: INonceRequestParams): Promise<IMythServerResponse<string>> {
        let url = getMythServerApi(mythServerApis.login.getNonce) + `?address=${params.address}`;
        const response: AxiosResponse<IMythServerResponse<string>> = await axios.get<IMythServerResponse<string>, AxiosResponse<IMythServerResponse<string>>>(url);
        return response.data;
    }

    public async requestTheLoginToken(params: INonceRequestParams): Promise<IMythServerResponse<ILoginResponse>> {
        let url = getMythServerApi(mythServerApis.login.requestLogin);
        const formData = new FormData();
        formData.append("address", params.address);
        if (params.signature) {
            formData.append("signature", params.signature);
        }
        const response: AxiosResponse<IMythServerResponse<ILoginResponse>> = await axios.post<IMythServerResponse<ILoginResponse>, AxiosResponse<IMythServerResponse<ILoginResponse>>>(url, formData);
        return response.data;
    }

    /*********************************************************** mintNew *********************************************************************/
    public async joinMintNew(actionId: number, passTokenId: number, account: string): Promise<IMythServerResponse<Boolean>> {
        let url = getMythServerApi(mythServerApis.mintNew.joinMintNew);
        const formData = new FormData();
        formData.append("actionId", actionId.toString());
        formData.append("passTokenId", passTokenId.toString());
        const response: AxiosResponse<IMythServerResponse<Boolean>> = await axios.post<IMythServerResponse<Boolean>, AxiosResponse<IMythServerResponse<Boolean>>>(url, formData, tokenHeaderConfig(account));
        return response.data;
    }


    public async getActiveActions(): Promise<IMythServerResponse<IGetActiveActionsResponse> | undefined> {
        let url = getMythServerApi(mythServerApis.mintNew.getActiveActions);
        const response: AxiosResponse<IMythServerResponse<IGetActiveActionsResponse>> = await axios.get<IMythServerResponse<IGetActiveActionsResponse>, AxiosResponse<IMythServerResponse<IGetActiveActionsResponse>>>(url);
        return response.data;
    }

    public async getPassStatus(passTokenIds: number[], account: string): Promise<IMythServerResponse<IGetUserPassesResponse> | undefined> {
        let url = getMythServerApi(mythServerApis.mintNew.getPassStatus);
        const formData = new FormData();
        formData.append("passTokenIds", passTokenIds.toString());
        const response: AxiosResponse<IMythServerResponse<IGetUserPassesResponse>> = await axios.post<IMythServerResponse<IGetUserPassesResponse>, AxiosResponse<IMythServerResponse<IGetUserPassesResponse>>>(url, formData, tokenHeaderConfig(account));
        return response.data;
    }

    public async getAllConfig(actionId: number, account: string): Promise<IMythServerResponse<IGetAllConfigResponse>> {
        let url = getMythServerApi(mythServerApis.mintNew.getAllConfig) + `?actionId=${actionId}`;
        const response: AxiosResponse<IMythServerResponse<IGetAllConfigResponse>> = await axios.get<IMythServerResponse<IGetAllConfigResponse>, AxiosResponse<IMythServerResponse<IGetAllConfigResponse>>>(url, tokenHeaderConfig(account));
        return response.data;
    }

    /*********************************************************** ipMarket *********************************************************************/
    public async getLicenseCollections(): Promise<IMythServerResponse<IGetLicenseCollectionsResponse>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getLicenseCollections);
        const response: AxiosResponse<IMythServerResponse<IGetLicenseCollectionsResponse>> = await axios.get<IMythServerResponse<IGetLicenseCollectionsResponse>, AxiosResponse<IMythServerResponse<IGetLicenseCollectionsResponse>>>(url);
        return response.data;
    }

    public async getAllIps(getAllIPsRequest: IGetAllIPsRequest): Promise<IMythServerResponse<IPagination<IPRsp>>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getAllIps);
        const formData = new FormData();
        formData.append("keywords", getAllIPsRequest.keywords);
        formData.append("filterIPL", getAllIPsRequest.filterIPL.toString());
        formData.append("licenseCollectionIds", getAllIPsRequest.licenseCollectionIds.toString());
        formData.append("pageNum", getAllIPsRequest.pageNum.toString());
        formData.append("pageSize", getAllIPsRequest.pageSize.toString());
        formData.append("orderField", getAllIPsRequest.orderField);
        formData.append("orderDirection", getAllIPsRequest.orderDirection);
        const response: AxiosResponse<IMythServerResponse<IPagination<IPRsp>>> = await axios.post<IMythServerResponse<IPagination<IPRsp>>, AxiosResponse<IMythServerResponse<IPagination<IPRsp>>>>(url, formData);
        return response.data;
    }

    public async getLicensedNFT(ipId: number): Promise<IMythServerResponse<ILicensedNFT>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getLicensedNFT);
        const formData = new FormData();
        formData.append("ipId", ipId.toString());
        const response: AxiosResponse<IMythServerResponse<ILicensedNFT>> = await axios.post<IMythServerResponse<ILicensedNFT>, AxiosResponse<IMythServerResponse<ILicensedNFT>>>(url, formData);
        return response.data;
    }

    public async getMintIPInfo(ipId: number, licensedNFT: boolean): Promise<IMythServerResponse<IMintIPInfo>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getMintIPInfo);
        const formData = new FormData();
        formData.append("ipId", ipId.toString());
        formData.append("licensedNFT", licensedNFT.toString());
        const response: AxiosResponse<IMythServerResponse<IMintIPInfo>> = await axios.post<IMythServerResponse<IMintIPInfo>, AxiosResponse<IMythServerResponse<IMintIPInfo>>>(url, formData);
        return response.data;
    }

    public async getIPsForLicensedNFT(request: IGetDerivativeWorksForLicenseNFTRequest): Promise<IMythServerResponse<IPagination<IPRsp>>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getIPsForLicensedNFT);
        const formData = new FormData();
        formData.append("licensedNFTId", request.licensedNFTId.toString());
        formData.append("pageNum", request.pageNum.toString());
        formData.append("pageSize", request.pageSize.toString());
        const response: AxiosResponse<IMythServerResponse<IPagination<IPRsp>>> = await axios.post<IMythServerResponse<IPagination<IPRsp>>, AxiosResponse<IMythServerResponse<IPagination<IPRsp>>>>(url, formData);
        return response.data;
    }

    public async getIPInfluences(licensedNFTId:number): Promise<IMythServerResponse<IPInfluence[]>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getIPInfluences);
        const formData = new FormData();
        formData.append("licensedNFTId", licensedNFTId.toString());
        const response: AxiosResponse<IMythServerResponse<IPInfluence[]>> = await axios.post<IMythServerResponse<IPInfluence[]>, AxiosResponse<IMythServerResponse<IPInfluence[]>>>(url,formData);
        return response.data;
    }

    public async getHomePageData(): Promise<IMythServerResponse<GetHomePageDataResponse>> {
        let url = getMythServerApi(mythServerApis.ipMarket.getHomePageData);
        const response: AxiosResponse<IMythServerResponse<GetHomePageDataResponse>> = await axios.get<IMythServerResponse<GetHomePageDataResponse>, AxiosResponse<IMythServerResponse<GetHomePageDataResponse>>>(url);
        return response.data;
    }

    public async getMyActivatedIPs(account:string): Promise<IMythServerResponse<IPagination<IPRsp>>> {
        let url = getMythServerApi(mythServerApis.user.getMyActivatedIPs) + "?page-num=1&page-size=100" ;
        const response: AxiosResponse<IMythServerResponse<IPagination<IPRsp>>> = await axios.get<IMythServerResponse<IPagination<IPRsp>>, AxiosResponse<IMythServerResponse<IPagination<IPRsp>>>>(url,tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    /*********************************************************** ipLicense *********************************************************************/
    public async licenseNFTs(account: string, licenseNFTsRequest: ILicenseNFTsRequest): Promise<IMythServerResponse<Boolean>> {
        let url = getMythServerApi(mythServerApis.ipLicense.licenseNFTs);
        const data = JSON.stringify(licenseNFTsRequest);
        const response: AxiosResponse<IMythServerResponse<Boolean>> = await axios.post<IMythServerResponse<Boolean>, AxiosResponse<IMythServerResponse<Boolean>>>(url, data, tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    public async activeLicenseNFT(account: string, ipId:number): Promise<IMythServerResponse<Boolean>> {
        const url = getMythServerApi(mythServerApis.ipLicense.activeLicenseNFT);
        const formData = new FormData();
        formData.append("ipId", ipId.toString());
        const response: AxiosResponse<IMythServerResponse<Boolean>> = await axios.post<IMythServerResponse<Boolean>, AxiosResponse<IMythServerResponse<Boolean>>>(url, formData, tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    public async addIPL(account: string, iplInfo: IPLInfo): Promise<IMythServerResponse<Boolean>> {
        const url = getMythServerApi(mythServerApis.ipLicense.addIPL);
        const data = JSON.stringify(iplInfo);
        const response: AxiosResponse<IMythServerResponse<Boolean>> = await axios.post<IMythServerResponse<Boolean>, AxiosResponse<IMythServerResponse<Boolean>>>(url, data, tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    public async updateIPL(account: string, iplInfo: IPLInfo): Promise<IMythServerResponse<Boolean>> {
        const url = getMythServerApi(mythServerApis.ipLicense.updateIPL);
        const data = JSON.stringify(iplInfo);
        const response: AxiosResponse<IMythServerResponse<Boolean>> = await axios.post<IMythServerResponse<Boolean>, AxiosResponse<IMythServerResponse<Boolean>>>(url, data, tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    public async deleteIPL(account: string, ipId:number): Promise<IMythServerResponse<Boolean>> {
        const url = getMythServerApi(mythServerApis.ipLicense.deleteIPL);
        const formData = new FormData();
        formData.append("ipId", ipId.toString());
        const response: AxiosResponse<IMythServerResponse<Boolean>> = await axios.post<IMythServerResponse<Boolean>, AxiosResponse<IMythServerResponse<Boolean>>>(url, formData, tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    public async getMyIPLs(status:number[], account: string, pageNum: number, pageSize: number): Promise<IMythServerResponse<IPagination<ILicensedNFT>>> {
        let url = getMythServerApi(mythServerApis.ipLicense.getMyIPLs);
        const formData = new FormData();
        formData.append("pageNum", pageNum.toString());
        formData.append("pageSize", pageSize.toString());
        formData.append("status", status.toString());
        const response: AxiosResponse<IMythServerResponse<IPagination<ILicensedNFT>>> = await axios.post<IMythServerResponse<IPagination<ILicensedNFT>>, AxiosResponse<IMythServerResponse<IPagination<ILicensedNFT>>>>(url, formData, tokenHeaderConfig(account));
        return response.data;
    }

    public async getIPLByIPId(account: string,ipId:number): Promise<IMythServerResponse<IPLInfo>> {
        let url = getMythServerApi(mythServerApis.ipLicense.getIPLByIPId);
        const formData = new FormData();
        formData.append("ipId", ipId.toString());
        const response: AxiosResponse<IMythServerResponse<IPLInfo>> = await axios.post<IMythServerResponse<IPLInfo>, AxiosResponse<IMythServerResponse<IPLInfo>>>(url, formData, tokenHeaderConfig(account));
        return response.data;
    }

    public async getLicensedNFTTokenIds(account: string, licenseCollectionId: number, tokenIds: string[]): Promise<IMythServerResponse<string[]>> {
        let url = getMythServerApi(mythServerApis.ipLicense.getLicensedNFTTokenIds);
        const formData = new FormData();
        formData.append("licenseCollectionId", licenseCollectionId.toString());
        formData.append("tokenIds", tokenIds.toString());
        const response: AxiosResponse<IMythServerResponse<string[]>> = await axios.post<IMythServerResponse<string[]>, AxiosResponse<IMythServerResponse<string[]>>>(url,formData, tokenHeaderConfig(account));
        return response.data;
    }
    /*********************************************************** nft brand *********************************************************************/
    public async getNFTBrands(licenseCollectionId: number,pageNum:number,pageSize:number): Promise<IMythServerResponse<IPagination<INFTBrandItem>>> {
        let url = getMythServerApi(mythServerApis.nftBrand.getNFTBrands) + "?license-collection-id="+ licenseCollectionId+"&page-num=" + pageNum + "&page-size=" + pageSize;
        const response: AxiosResponse<IMythServerResponse<IPagination<INFTBrandItem>>> = await axios.get<IMythServerResponse<IPagination<INFTBrandItem>>, AxiosResponse<IMythServerResponse<IPagination<INFTBrandItem>>>>(url);
        return response.data;
    }

    public async getNFTBrand(nftBrandId: number): Promise<IMythServerResponse<INFTBrand>> {
        let url = getMythServerApi(mythServerApis.nftBrand.getNFTBrand) + nftBrandId;
        const response: AxiosResponse<IMythServerResponse<INFTBrand>> = await axios.get<IMythServerResponse<INFTBrand>, AxiosResponse<IMythServerResponse<INFTBrand>>>(url);
        return response.data;
    }

    /*********************************************************** user *********************************************************************/
    public async getUserInfo(account: string): Promise<IMythServerResponse<IGetUserInfoResponse>> {
        let url = getMythServerApi(mythServerApis.user.getUserInfo);
        const response: AxiosResponse<IMythServerResponse<IGetUserInfoResponse>> = await axios.get<IMythServerResponse<IGetUserInfoResponse>, AxiosResponse<IMythServerResponse<IGetUserInfoResponse>>>(url, tokenHeaderConfig(account, ContentType.JSON));
        return response.data;
    }

    public async updateUserInfo(account: string, userInfoRequest: IUserInfoRequest): Promise<IMythServerResponse<boolean>> {
        let url = getMythServerApi(mythServerApis.user.updateUserInfo);
        const data = JSON.stringify(userInfoRequest);
        const response: AxiosResponse<IMythServerResponse<boolean>> = await axios.put<IMythServerResponse<boolean>, AxiosResponse<IMythServerResponse<boolean>>>(url, data, tokenHeaderConfig(account));
        return response.data;
    }
}
